/**
 * Shopify resource identifiers come in two flavors:
 *   - Global ID (GID) — `gid://shopify/<Type>/<id>`, returned by GraphQL
 *     APIs and Shopify's `resourcePicker`.
 *   - Bare numeric id — what cart line items, REST endpoints, and most
 *     of our internal pipeline operate on.
 *
 * These helpers normalize between the two flavors so persisted data and
 * widget runtime always carry bare ids, and we only reconstitute a GID
 * at the boundary where Shopify needs it (e.g. picker `selectionIds`,
 * GraphQL mutations).
 *
 * The BE has near-duplicate `stripShopifyGid` / `stripGid` helpers in
 * `forms-bootstrap.service.ts`, `forms.service.ts`, and
 * `upsell-pricing.utils.ts`. Those should be replaced by importing from
 * here in a follow-up PR (kept untouched in this change to limit blast
 * radius).
 */

const SHOPIFY_GID_PREFIX_RE = /^gid:\/\/shopify\/[^/]+\//;

/**
 * Strips Shopify's GID prefix, returning the bare id. Idempotent —
 * already-bare ids are returned unchanged.
 *
 * Example: `"gid://shopify/Product/10184657043777"` → `"10184657043777"`
 */
export function stripShopifyGid(gid: string): string {
  return gid.replace(SHOPIFY_GID_PREFIX_RE, "");
}

/**
 * Reconstitutes a Shopify Product GID from a bare id, for callers that
 * need the full GID format (e.g. `resourcePicker({ selectionIds })`,
 * which uses GIDs for pre-selection round-tripping). Idempotent — if
 * the input already starts with `gid://shopify/`, it's returned as-is.
 */
export function toShopifyProductGid(id: string): string {
  if (SHOPIFY_GID_PREFIX_RE.test(id)) return id;
  return `gid://shopify/Product/${id}`;
}
